import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';
import { useTimeTrackingFunctions } from 'Context/TimeTracking';

import * as XLSX from 'xlsx';
import classes from './timesheetReports.module.css';

interface UserDateEntry {
  date: string;
  hours: number;
}

interface UserGroup {
  name: string;
  entries: UserDateEntry[];
  totalHours: number;
}

interface ProjectGroup {
  uid: string;
  users: UserGroup[];
  totalHours: number;
}

function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0 && m === 0) return '0h';
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function TimesheetReports() {
  const requestVersion = useRef(0);

  const {
    savedEmployeeFilters,
    savedAddressFilters,
    savedProjectNumFilters,
    savedDateFilter,
    employeeFilters,
    projectNumberFilters,
  }: any = useTimeTrackingFunctions();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    requestVersion.current += 1;
    const thisRequest = requestVersion.current;

    setLoading(true);
    setError('');

    const filters: any[] = [];

    if (savedDateFilter) {
      filters.push({ field: 'timecard.time_in', operator: '>=', value: savedDateFilter.startDate });
      filters.push({ field: 'timecard.time_in', operator: '<=', value: savedDateFilter.endDate });
    }
    if (savedProjectNumFilters && savedProjectNumFilters.length > 0) {
      const project = projectNumberFilters?.find((p: any) => p.id === savedProjectNumFilters[0]);
      if (project) {
        filters.push({ field: 'project.uid', operator: '=', value: project.name });
      }
    }
    if (savedEmployeeFilters && savedEmployeeFilters.length > 0) {
      const employee = employeeFilters?.find((e: any) => e.id === savedEmployeeFilters[0]);
      if (employee) {
        const nameParts = employee.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        if (firstName) filters.push({ field: 'user.first_name', operator: '=', value: firstName });
        if (lastName) filters.push({ field: 'user.last_name', operator: '=', value: lastName });
      }
    }

    const perPage = 100;
    const payload = {
      root_entity: 'timecards',
      columns: ['user.first_name', 'user.last_name', 'timecard.total_hours', 'timecard.time_in', 'project.uid'],
      filters,
      sorts: [{ field: 'project.uid', direction: 'asc' }],
      per_page: perPage,
    };

    try {
      let allRows: any[] = [];
      let page = 1;
      let lastPage = 1;

      do {
        const response = await Api.post('/custom-reports/preview', { ...payload, page });
        if (thisRequest !== requestVersion.current) return;

        const responseData = response?.data;
        allRows = allRows.concat(responseData?.data ?? []);
        lastPage = responseData?.last_page || 1;
        page += 1;
      } while (page <= lastPage);

      setData(allRows);
    } catch (e: any) {
      if (thisRequest !== requestVersion.current) return;
      setError(e?.message || 'Failed to load report data.');
    } finally {
      if (thisRequest === requestVersion.current) {
        setLoading(false);
      }
    }
  }, [savedDateFilter, savedEmployeeFilters, savedAddressFilters, savedProjectNumFilters, employeeFilters, projectNumberFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const projectGroups: ProjectGroup[] = useMemo(() => {
    if (!data || data.length === 0) return [];

    const projects = new Map<string, Map<string, UserDateEntry[]>>();

    data.forEach((row: any) => {
      const project = row['project.uid'] || 'Unassigned';
      const firstName = row['user.first_name'] || '';
      const lastName = row['user.last_name'] || '';
      const name = `${firstName} ${lastName}`.trim() || 'Unknown';
      const hours = parseFloat(row['timecard.total_hours']) || 0;
      const timeIn = row['timecard.time_in'] || '';
      const date = timeIn ? timeIn.split('T')[0] : '';

      if (!projects.has(project)) projects.set(project, new Map());
      const userMap = projects.get(project)!;
      if (!userMap.has(name)) userMap.set(name, []);
      userMap.get(name)!.push({ date, hours });
    });

    return Array.from(projects.entries())
      .map(([uid, userMap]) => {
        const users: UserGroup[] = Array.from(userMap.entries())
          .map(([name, entries]) => {
            const sorted = entries.sort((a, b) => b.date.localeCompare(a.date));
            const totalHours = entries.reduce((sum, e) => sum + e.hours, 0);
            return { name, entries: sorted, totalHours };
          })
          .sort((a, b) => b.totalHours - a.totalHours);
        const totalHours = users.reduce((sum, u) => sum + u.totalHours, 0);
        return { uid, users, totalHours };
      })
      .sort((a, b) => b.totalHours - a.totalHours);
  }, [data]);

  const exportXls = useCallback(() => {
    if (projectGroups.length === 0) return;

    const rows: (string | number)[][] = [];
    projectGroups.forEach((group) => {
      rows.push([group.uid, '', formatHours(group.totalHours)]);
      group.users.forEach((user) => {
        rows.push(['', user.name, formatHours(user.totalHours)]);
        user.entries.forEach((entry) => {
          rows.push(['', `    ${entry.date}`, formatHours(entry.hours)]);
        });
      });
      rows.push([]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Set column widths
    ws['!cols'] = [{ wch: 16 }, { wch: 30 }, { wch: 12 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet Report');
    XLSX.writeFile(wb, 'timesheet-report.xlsx');
  }, [projectGroups]);

  return (
    <div className={classes.container}>
      {error && <p className={classes.errorState}>{error}</p>}

      {loading && !error && <p className={classes.loadingState}>Loading report data...</p>}

      {!loading && !error && projectGroups.length === 0 && (
        <p className={classes.emptyState}>No timecard data found for the selected filters.</p>
      )}

      {!loading && !error && projectGroups.length > 0 && (
        <>
        <div className={classes.toolbar}>
          <button type="button" className={classes.exportButton} onClick={exportXls}>
            Export Excel
          </button>
        </div>
        <div className={classes.groupList}>
          {projectGroups.map((group) => (
            <div key={group.uid} className={classes.projectGroup}>
              <div className={classes.projectHeader}>
                <span className={classes.projectUid}>{group.uid}</span>
                <span className={classes.projectTotal}>{formatHours(group.totalHours)}</span>
              </div>
              <div className={classes.userList}>
                {group.users.map((user) => (
                  <div key={user.name} className={classes.userGroup}>
                    <div className={classes.userRow}>
                      <span className={classes.userName}>{user.name}</span>
                      <span className={classes.userHours}>{formatHours(user.totalHours)}</span>
                    </div>
                    {user.entries.map((entry, idx) => (
                      <div key={`${entry.date}-${idx}`} className={classes.dateRow}>
                        <span className={classes.dateText}>{entry.date}</span>
                        <span className={classes.dateHours}>{formatHours(entry.hours)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}

const TimesheetReportsMemo = memo(TimesheetReports, areEqual);
export { TimesheetReportsMemo as TimesheetReports };
